import _ from 'lodash';
import AccountType from 'scripts/constants/AccountType';
import RegistrationStep from 'scripts/constants/RegistrationStep';
import {
  acceptPdsUrl,
  accountApplicationInReviewUrl,
  accountUrl,
  advisorUrl,
  depositUrl,
  emailVerificationUrl,
  identityVerificationUrl,
  onboardingTmdUrl,
  signupUrl,
  vedaFailUrl,
} from 'src/utils/pageUrls';

export const hasNominatedBankAccount = (user) =>
  !!(user.financialDetails && user.financialDetails.bankAccountDetails);

export const hasTfnProvided = (user) => {
  return _.isNil(user.financialDetails) ? false : user.financialDetails.hasTfn;
};

export const hasBasicEmailSubscription = (user) => {
  return _.isNil(user.emailSubscriptions)
    ? false
    : user.emailSubscriptions.basic;
};

export const hasFirstDistributionCollected = (user) => {
  return user.firstDistributionCollected;
};

export const isInvestor = (user) => {
  return _.isNil(user)
    ? false
    : user.nextRegistrationStep === RegistrationStep.DONE;
};

export const isMember = (user) => {
  return _.isNil(user)
    ? false
    : user.nextRegistrationStep === RegistrationStep.INVEST ||
        user.nextRegistrationStep === RegistrationStep.DONE;
};

export const hasBricksOnSoldoutProperty = (account) => {
  return _.isNil(account)
    ? false
    : !_.every(account.portfolio.records, [
        'brickPriceType',
        'LOWEST_AVAILABLE',
      ]);
};

export const isUserLoggedIn = (user) => {
  return user && user.loggedIn;
};

export const isUserWithAdminRole = (user) => {
  return user && user.loggedIn && user.email.endsWith('@brickx.com');
};

export const isAdvisorAccount = (user) =>
  user && user.accountType === AccountType.ADVISOR;

export const getDefaultAuthenticatedPath = (user) => {
  return isAdvisorAccount(user) ? advisorUrl() : accountUrl();
};

export const hasUserAcceptedPds = (user) => {
  return (
    isUserLoggedIn(user) &&
    user.nextRegistrationStep !== RegistrationStep.VERIFY &&
    user.nextRegistrationStep !== RegistrationStep.SIGNUP &&
    user.nextRegistrationStep !== RegistrationStep.PDS
  );
};

const redirectIncompleteRegistration = (user, router) => {
  if (!isUserLoggedIn(user)) {
    router.push(signupUrl());
    return true;
  }

  const redirectUrl = {
    [RegistrationStep.VERIFY]: emailVerificationUrl(),
    [RegistrationStep.PDS]: acceptPdsUrl(),
    [RegistrationStep.SETUP]: identityVerificationUrl(),
    [RegistrationStep.VEDA]: vedaFailUrl(),
    [RegistrationStep.TMD]: onboardingTmdUrl(),
    [RegistrationStep.APPLICATION_REVIEW]: accountApplicationInReviewUrl(),
  }[user.nextRegistrationStep];

  if (redirectUrl) {
    router.push(redirectUrl);
    return true;
  }

  return false;
};

export const checkUserWithBuyPermission = (user, router) => {
  if (redirectIncompleteRegistration(user, router)) return false;
  if (user.nextRegistrationStep === RegistrationStep.DEPOSIT) {
    router.push(depositUrl());
    return false;
  }
  return [RegistrationStep.INVEST, RegistrationStep.DONE].includes(
    user.nextRegistrationStep,
  );
};

export const checkUserWithSellPermission = (user, router) => {
  if (redirectIncompleteRegistration(user, router)) return false;
  return [
    RegistrationStep.DEPOSIT,
    RegistrationStep.INVEST,
    RegistrationStep.DONE,
  ].includes(user.nextRegistrationStep);
};

export const hasMinorDetails = (user) =>
  user.accountType === 'MINOR' && user.minorDetails;

export const canSetupMinorAccount = (user) =>
  user.accountType !== 'MINOR' && hasUserAcceptedPds(user);

export const canApplySmartRenter = (user) =>
  user.accountType === AccountType.INDIVIDUAL ||
  user.accountType === AccountType.INTERNAL;

export const isMinorAccount = (user) =>
  user && user.accountType === AccountType.MINOR;

export const isSMSForTrustorCompanyAccount = (user) =>
  user &&
  (user.accountType === AccountType.SMSF ||
    user.accountType === AccountType.TRUST ||
    user.accountType === AccountType.CORPORATION);

export const canSetupWholesale = (user) =>
  user.accountType === AccountType.INDIVIDUAL && hasUserAcceptedPds(user);

export const isUserBoAllVerified = (user) => {
  if (isSMSForTrustorCompanyAccount(user)) {
    if (
      user.boDetails &&
      user.boDetails.length > 0 &&
      user.boDetails.filter((ele) => !ele.idChecked).length > 0
    ) {
      return false;
    }
  }
  return true;
};
